import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";
import { authErrorMessage, useAuth } from "@/auth/AuthProvider";
import { Copy } from "@/copy/copy";
import { AuthLayout, FieldError } from "./AuthLayout";

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Enter your email.")
    .email("That doesn't look like an email address."),
});
type Values = z.infer<typeof schema>;

/** Codes that only say whether an account exists; we answer the same either way. */
const SILENT = new Set(["auth/user-not-found", "auth/invalid-credential"]);

export function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: "" } });

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (error) {
      if (SILENT.has((error as { code?: string })?.code ?? "")) setSent(true);
      else setFormError(authErrorMessage(error));
    }
  });

  return (
    <AuthLayout title="auth.forgot.h1" sub="auth.forgot.sub" testId="forgot-page">
      {sent ? (
        <>
          <Copy k="auth.forgot.sent" as="p" className="wp-note" data-testid="forgot-sent" />
          <div className="wp-auth-links">
            <Link to="/signin">Back to sign in</Link>
          </div>
        </>
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <label className="wp-field">
            Email
            <input
              type="email"
              autoComplete="email"
              {...register("email")}
              aria-invalid={!!errors.email}
              data-testid="forgot-email"
            />
            <FieldError message={errors.email?.message} />
          </label>
          {formError && (
            <p className="wp-form-err" role="alert">
              {formError}
            </p>
          )}
          <button
            type="submit"
            className="wp-btn"
            disabled={isSubmitting}
            data-testid="forgot-submit"
          >
            {isSubmitting ? "Sending…" : "Send the reset link"}
          </button>
          <div className="wp-auth-links">
            <Link to="/signin">Back to sign in</Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
